import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Sparkles, Loader2, Printer, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Lab 8 — Engineering Image / Infographic Generation.
 * Guides the student through the manual's steps 70-77:
 *   1. Topic + Prompt v1  → generate IMAGE V1 via existing /api/content/image
 *   2. Agent critiques the prompt + proposes a refined prompt
 *   3. Student runs refined prompt → generate FINAL image
 *   4. Agent returns a verification note template
 *   5. Save deliverable PDF (window.print)
 */
export default function LabImage() {
  const [topic, setTopic] = useState('');
  const [promptV1, setPromptV1] = useState('');
  const [imgV1, setImgV1] = useState(null);
  const [critique, setCritique] = useState(null);
  const [promptV2, setPromptV2] = useState('');
  const [imgFinal, setImgFinal] = useState(null);
  const [verifyNote, setVerifyNote] = useState(null);
  const [verifyText, setVerifyText] = useState('');
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const call = async (path, body) => {
    const r = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.detail || `Request failed (${r.status})`);
    return data;
  };

  const generateImage = async (prompt, slot) => {
    setLoading(slot);
    setError('');
    try {
      const data = await call('/api/content/image', {
        website: 'saketh.ai',
        prompt,
        style: 'professional',
      });
      const url = data.image_url || data.url || data.result_url || null;
      if (!url) throw new Error('No image URL in response.');
      if (slot === 'v1') setImgV1(url);
      else setImgFinal(url);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const askCritique = async () => {
    setLoading('critique');
    setError('');
    try {
      const data = await call('/api/labs/8/ask', {
        kind: 'critique_image_prompt',
        context: { topic, draft_prompt: promptV1 },
      });
      setCritique(data);
      if (data.refined_prompt) setPromptV2(data.refined_prompt);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const askVerify = async () => {
    setLoading('verify');
    setError('');
    try {
      const data = await call('/api/labs/8/ask', {
        kind: 'verification_note',
        context: { topic, final_prompt: promptV2 || promptV1 },
      });
      setVerifyNote(data);
      const seed = [
        'Accurate elements I see: ' + (data.accurate_elements || []).join('; '),
        'Things I must check manually: ' + (data.human_must_check || []).join('; '),
        'Copyright / IP note: ' + (data.copyright_note || ''),
      ].join('\n\n');
      setVerifyText(seed);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const exportPDF = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const safe = (s) => (s || '').toString().replace(/</g, '&lt;');
    w.document.write(`<html><head><title>Lab 8 — Deliverable</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:28px;color:#111;max-width:780px;margin:0 auto;}
        h1,h2{line-height:1.25;margin-top:1.4em;}
        img{max-width:100%;border-radius:6px;border:1px solid #e5e7eb;margin:8px 0;}
        pre{background:#f8fafc;color:#111;padding:12px;border-radius:8px;border:1px solid #e2e8f0;white-space:pre-wrap;}
      </style></head><body>
      <h1>Lab 8 — Engineering Image / Infographic Generation</h1>
      <p><strong>Topic:</strong> ${safe(topic)}</p>
      <h2>Prompt v1</h2><pre>${safe(promptV1)}</pre>
      ${imgV1 ? `<img src="${imgV1}" alt="Image v1" />` : ''}
      ${critique?.likely_issues ? `<h2>Issues identified</h2><ul>${critique.likely_issues.map(i=>`<li>${safe(i)}</li>`).join('')}</ul>` : ''}
      <h2>Refined prompt</h2><pre>${safe(promptV2 || promptV1)}</pre>
      ${imgFinal ? `<img src="${imgFinal}" alt="Final image" />` : ''}
      <h2>Verification note</h2><pre>${safe(verifyText)}</pre>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const Step = ({ n, title, children }) => (
    <section style={{ marginTop: 20, padding: 18, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: 14, background: '#8b5cf6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>{n}</div>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
      </div>
      {children}
    </section>
  );

  const Btn = ({ onClick, disabled, children, slot, variant = 'primary', dataTestId }) => (
    <button
      type="button" onClick={onClick} disabled={disabled} data-testid={dataTestId}
      style={{
        padding: '8px 14px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500,
        background: variant === 'primary' ? '#8b5cf6' : 'transparent',
        color: variant === 'primary' ? '#fff' : '#8b5cf6',
        border: variant === 'primary' ? 'none' : '1px solid #8b5cf6',
        opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {loading === slot ? <Loader2 size={13} className="spin-l" /> : children}
    </button>
  );

  const txtArea = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, resize: 'vertical' };

  return (
    <div style={{ padding: '1.5rem 1.5rem 3rem', maxWidth: 900, margin: '0 auto' }} data-testid="lab-8">
      <Link to="/labs" style={{ color: '#8b5cf6', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, textDecoration: 'none', marginBottom: 6 }}><ArrowLeft size={14} /> Back to Labs</Link>
      <h1 style={{ margin: '4px 0 2px', fontSize: 24, fontWeight: 600 }}>Lab 8 — Engineering Image / Infographic Generation</h1>
      <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>Follow manual steps 70–77. Prompt → image → critique → refine → final → verification note → PDF.</p>

      {error && <div data-testid="lab-error" style={{ marginTop: 10, padding: '8px 12px', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: 8, color: '#b91c1c', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={13} /> {error}</div>}

      <Step n={1} title="Topic + Prompt v1 (Steps 70–72)">
        <label style={{ fontSize: 12, fontWeight: 500 }}>Engineering topic</label>
        <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Solar inverter + battery wiring diagram" style={{ ...txtArea, marginTop: 4, marginBottom: 10 }} data-testid="lab-topic" />
        <label style={{ fontSize: 12, fontWeight: 500 }}>Draft prompt (include subject, audience, layout, labels, style, aspect ratio)</label>
        <textarea rows={4} value={promptV1} onChange={e => setPromptV1(e.target.value)} style={{ ...txtArea, marginTop: 4 }} data-testid="lab-prompt-v1" placeholder="Example: Infographic for engineering students; left-to-right flow of solar → inverter → battery; label each; flat vector style; 16:9." />
        <div style={{ marginTop: 10 }}>
          <Btn onClick={() => generateImage(promptV1, 'v1')} disabled={!promptV1.trim() || !!loading} slot="v1" dataTestId="lab-generate-v1"><ImagePlus size={13} /> Generate IMAGE v1</Btn>
        </div>
        {imgV1 && <img src={imgV1} alt="v1" style={{ marginTop: 12, maxWidth: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }} data-testid="lab-image-v1" />}
      </Step>

      <Step n={2} title="Critique the prompt (Step 73)">
        <Btn onClick={askCritique} disabled={!promptV1.trim() || !!loading} slot="critique" variant="secondary" dataTestId="lab-critique-btn"><Sparkles size={13} /> Ask the agent to critique</Btn>
        {critique?.likely_issues && (
          <div style={{ marginTop: 10, padding: 12, background: '#fef3c720', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 13 }}>
            <strong>Likely issues:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>{critique.likely_issues.map((i, k) => <li key={k}>{i}</li>)}</ul>
          </div>
        )}
      </Step>

      <Step n={3} title="Refined prompt + FINAL image (Steps 74–76)">
        <label style={{ fontSize: 12, fontWeight: 500 }}>Refined prompt {critique && <em style={{ color: '#059669', fontWeight: 400 }}>— pre-filled by the critique</em>}</label>
        <textarea rows={4} value={promptV2} onChange={e => setPromptV2(e.target.value)} style={{ ...txtArea, marginTop: 4 }} data-testid="lab-prompt-v2" />
        <div style={{ marginTop: 10 }}>
          <Btn onClick={() => generateImage(promptV2 || promptV1, 'final')} disabled={!(promptV2 || promptV1).trim() || !!loading} slot="final" dataTestId="lab-generate-final"><ImagePlus size={13} /> Generate FINAL image</Btn>
        </div>
        {imgFinal && <img src={imgFinal} alt="final" style={{ marginTop: 12, maxWidth: '100%', borderRadius: 8, border: '1px solid #e5e7eb' }} data-testid="lab-image-final" />}
      </Step>

      <Step n={4} title="Verification note (Step 77)">
        <Btn onClick={askVerify} disabled={!imgFinal || !!loading} slot="verify" variant="secondary" dataTestId="lab-verify-btn"><Sparkles size={13} /> Generate verification note draft</Btn>
        <textarea rows={6} value={verifyText} onChange={e => setVerifyText(e.target.value)} style={{ ...txtArea, marginTop: 10 }} placeholder="What's accurate? What must a human still check? Copyright note." data-testid="lab-verify-text" />
      </Step>

      <Step n={5} title="Export deliverable">
        <Btn onClick={exportPDF} disabled={!imgFinal || !verifyText.trim()} slot="pdf" variant="secondary" dataTestId="lab-export-pdf"><Printer size={13} /> Save submission PDF</Btn>
      </Step>

      <style>{`.spin-l{animation:spin-l .9s linear infinite}@keyframes spin-l{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
